import React from 'react';
import {
  Alert,
  Box,
  Button,
  ChevronDownIcon,
  ChevronUpIcon,
  ExternalLinkIcon,
  Heading,
  Text,
} from '@razorpay/blade/components';
import { EmployeeFieldAccess, EmployeeFieldAccessRole } from '../../../types';
import { NoMemberRolesToggle } from '../../../styles';
import { RolePermissionsPanel } from './RolePermissionsPanel';

export type PermissionsStepProps = {
  isRolesLoading: boolean;
  isRolesError: boolean;
  onRetryRoles: () => void;
  rolesWithMembers: EmployeeFieldAccessRole[];
  rolesWithoutMembers: EmployeeFieldAccessRole[];
  resolvedAccessPolicy: Record<number, EmployeeFieldAccess>;
  openAccessRoleId: number | null;
  onAccessMenuToggle: (roleId: number) => void;
  onAccessSelect: (roleId: number, access: EmployeeFieldAccess) => void;
  showNoMemberRoles: boolean;
  onToggleNoMemberRoles: () => void;
};

export const PermissionsStep = ({
  isRolesLoading,
  isRolesError,
  onRetryRoles,
  rolesWithMembers,
  rolesWithoutMembers,
  resolvedAccessPolicy,
  openAccessRoleId,
  onAccessMenuToggle,
  onAccessSelect,
  showNoMemberRoles,
  onToggleNoMemberRoles,
}: PermissionsStepProps) => {
  const panelProps = {
    resolvedAccessPolicy,
    openAccessRoleId,
    onAccessMenuToggle,
    onAccessSelect,
  };

  return (
    <Box padding={['spacing.8', 'spacing.7', 'spacing.6']} maxWidth="560px">
      <Heading size="large">Permissions</Heading>
      <Text marginTop="spacing.2" color="surface.text.gray.muted">
        Choose who can see and edit the value on an employee profile.
      </Text>
      <Box marginTop="spacing.3">
        <Button
          variant="tertiary"
          icon={ExternalLinkIcon}
          iconPosition="right"
          href="/allUserRoles"
          target="_blank">
          View all roles
        </Button>
      </Box>
      {isRolesError ? (
        <Box marginTop="spacing.5">
          <Alert
            color="negative"
            title="Could not load roles"
            description="Retry to choose who can view or edit this field."
            actions={{ primary: { text: 'Retry', onClick: onRetryRoles } }}
          />
        </Box>
      ) : isRolesLoading ? (
        <Box marginTop="spacing.5">
          <Alert
            color="information"
            title="Loading roles"
            description="Fetching roles for field permission selection."
          />
        </Box>
      ) : (
        <Box marginTop="spacing.5">
          <RolePermissionsPanel roles={rolesWithMembers} {...panelProps} />
          {rolesWithoutMembers.length > 0 ? (
            <>
              <NoMemberRolesToggle
                type="button"
                aria-expanded={showNoMemberRoles}
                onClick={onToggleNoMemberRoles}>
                <Text weight="semibold">{rolesWithoutMembers.length} roles with no members</Text>
                {showNoMemberRoles ? (
                  <ChevronUpIcon size="medium" color="surface.icon.gray.muted" />
                ) : (
                  <ChevronDownIcon size="medium" color="surface.icon.gray.muted" />
                )}
              </NoMemberRolesToggle>
              {showNoMemberRoles ? (
                <Box marginTop="spacing.3">
                  <RolePermissionsPanel roles={rolesWithoutMembers} {...panelProps} />
                </Box>
              ) : null}
            </>
          ) : null}
        </Box>
      )}
    </Box>
  );
};
